import { INSTALL_BLOCK_GATE_IDS, READINESS_GATE_DEFINITIONS } from '@/constants/readiness-gates';
import type {
  LocationReadinessGate,
  ReadinessGateId,
  ReadinessGateStatus,
} from '@/types/readiness-gates';

export type ReadinessGateRow = {
  id: string;
  location_id: string;
  gate_id: ReadinessGateId;
  status: ReadinessGateStatus;
  owner: string | null;
  due_date: string | null;
  completed_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
};

export function isReadinessGateClosed(status: ReadinessGateStatus): boolean {
  return status === 'complete' || status === 'waived';
}

export function mergeLocationReadinessGates(
  locationId: string,
  rows: ReadinessGateRow[]
): LocationReadinessGate[] {
  const rowByGateId = new Map(rows.map((row) => [row.gate_id, row]));

  return READINESS_GATE_DEFINITIONS.map((definition) => {
    const row = rowByGateId.get(definition.gateId);
    return {
      id: row?.id ?? null,
      location_id: locationId,
      gate_id: definition.gateId,
      status: row?.status ?? 'not_started',
      owner: row?.owner ?? definition.defaultOwner,
      due_date: row?.due_date ?? null,
      completed_at: row?.completed_at ?? null,
      notes: row?.notes ?? '',
      label: definition.label,
      blocksLabel: definition.blocksLabel,
      defaultOwner: definition.defaultOwner,
      blocksInstall: definition.blocksInstall,
      created_at: row?.created_at ?? null,
      updated_at: row?.updated_at ?? null,
    };
  });
}

export function computeReadinessProgress(gates: LocationReadinessGate[]): {
  completeCount: number;
  totalCount: number;
  percent: number;
} {
  const totalCount = gates.length;
  const completeCount = gates.filter((gate) => isReadinessGateClosed(gate.status)).length;
  const percent = totalCount === 0 ? 0 : Math.round((completeCount / totalCount) * 100);

  return { completeCount, totalCount, percent };
}

export function getInstallBlockingGates(gates: LocationReadinessGate[]): LocationReadinessGate[] {
  return gates.filter(
    (gate) => gate.blocksInstall && INSTALL_BLOCK_GATE_IDS.includes(gate.gate_id) && !isReadinessGateClosed(gate.status)
  );
}

export function isInstallBlocked(gates: LocationReadinessGate[]): boolean {
  return getInstallBlockingGates(gates).length > 0;
}

export function resolveCompletedAtForStatus(
  status: ReadinessGateStatus,
  previousCompletedAt: string | null
): string | null {
  if (status === 'complete') {
    return previousCompletedAt ?? new Date().toISOString();
  }
  if (status === 'waived') {
    return previousCompletedAt;
  }
  return null;
}
